import { ReactNode, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useManagingOrgUserSites } from "@/components/user-context/helpers/cohorts";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";
import { FeatureExposed } from "@/utils/feature-exposed/use-feature-exposed";

import { ExistingUser } from "../existing-user/existing-user";

import { SitesList } from "./components/sites-list";

export const ManagedUser = () => {
    const [showSiteNaming, setShowSiteNaming] = useState(false);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { formatMessage } = useIntl();

    const sites = useManagingOrgUserSites();
    const { profile, product } = useUserContext();
    const orgId = profile?.attributes?.managingOrg?.orgId;

    const clickGoToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "guardGoToReturnToProductScreen",
            attributes: {
                orgId,
                productName: product?.name
            }
        }).fire();
    };

    const goToExistingWorkspaceMessageValues = {
        link: (str: ReactNode) =>
            goToExistingWorkspace(str, clickGoToExistingWorkspace)
    };

    const goToExistingWorkspaceMessage = formatMessage(
        {
            id: "bxpSignup.companyExpand.cobrandedReturnToJoinSites",
            defaultMessage: "or <link>join an existing site</link>"
        },
        goToExistingWorkspaceMessageValues
    );

    const clickGoToSiteNamingScreen = () => {
        setShowSiteNaming(true);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "guardGoToSiteNamingScreen",
            attributes: {
                orgId,
                productName: product?.name
            }
        }).fire();
    };

    const goToSiteNamingMessageValues = {
        link: (str: ReactNode) => goToSiteNaming(str, clickGoToSiteNamingScreen)
    };

    const goToSiteNamingMessage = formatMessage(
        {
            id: "BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink",
            defaultMessage: "or <link>start a new site</link>"
        },
        goToSiteNamingMessageValues
    );

    return sites?.length ? (
        <FeatureExposed
            experimentKey="signup-guard-return-to-product"
            group="treatment"
            metadata={{
                orgId
            }}
        >
            {showSiteNaming ? (
                <div css={containerCss}>
                    <ExistingUser />
                    <div css={goToMessageCss}>
                        {goToExistingWorkspaceMessage}
                    </div>
                </div>
            ) : (
                <div css={containerCss}>
                    <SitesList sites={sites} />
                    <div css={goToMessageCss}>{goToSiteNamingMessage}</div>
                </div>
            )}
        </FeatureExposed>
    ) : null;
};

const goToSiteNaming = (
    str: ReactNode,
    clickGoToSiteNamingScreen: () => void
) => (
    <Button css={goToLinkCss} onClick={clickGoToSiteNamingScreen}>
        {str}
    </Button>
);

const goToExistingWorkspace = (
    str: ReactNode,
    clickGoToExistingWorkspace: () => void
) => (
    <Button css={goToLinkCss} onClick={clickGoToExistingWorkspace}>
        {str}
    </Button>
);

const containerCss = css({
    alignContent: "space-between",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    gap: 16,
    justifyContent: "space-between"
});

const goToLinkCss = css({
    backgroundColor: "transparent !important",
    border: "0 !important",
    borderRadius: "0 !important",
    color: wacToken("color.primary.blue"),
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const goToMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});
